import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import type { EmojiClickData } from 'emoji-picker-react';
import { DT, DS2 } from '../../../theme/designTokens';
import { showSocialToast } from '../../social/SocialToastManager';
import { uiFromError, renderUiMessage } from '../../../i18n/uiMessage';
import { htmlToFormattedText } from '../../../utils/htmlToFormattedText';
import { useTranslation } from '../../../i18n/react';

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const EDITOR_MAX_HEIGHT = 140;

function ToolbarIcon({ size = 17, children }: { size?: number; children: React.ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {children}
    </svg>
  );
}

function BoldIcon() {
  return (
    <ToolbarIcon>
      <path d="M7 5h6a3.5 3.5 0 0 1 0 7H7zM7 12h7a3.5 3.5 0 0 1 0 7H7z" stroke="#A89F86" strokeWidth="1.8" strokeLinejoin="round" />
    </ToolbarIcon>
  );
}

function ItalicIcon() {
  return (
    <ToolbarIcon>
      <path d="M10 5h7M6 19h7M13 5l-4 14" stroke="#A89F86" strokeWidth="1.8" strokeLinecap="round" />
    </ToolbarIcon>
  );
}

function UnderlineIcon() {
  return (
    <ToolbarIcon>
      <path d="M6 4v7a5 5 0 0 0 10 0V4M5 20h13" stroke="#A89F86" strokeWidth="1.8" strokeLinecap="round" />
    </ToolbarIcon>
  );
}

function LinkIcon() {
  return (
    <ToolbarIcon>
      <path d="M9.5 14.5l5-5M8 12l-2.5 2.5a3 3 0 0 0 4.24 4.24L12 16.5M16 12l2.5-2.5a3 3 0 0 0-4.24-4.24L12 7.5" stroke="#A89F86" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </ToolbarIcon>
  );
}

function HashtagIcon() {
  return (
    <ToolbarIcon>
      <path d="M9 4l-2 16M17 4l-2 16M4 9h16M3 15h16" stroke="#A89F86" strokeWidth="1.8" strokeLinecap="round" />
    </ToolbarIcon>
  );
}

function EmojiIcon() {
  return (
    <ToolbarIcon>
      <circle cx="12" cy="12" r="8.5" stroke="#A89F86" strokeWidth="1.8" />
      <path d="M9 10h.01M15 10h.01M8.5 14c1 1.4 2.3 2 3.5 2s2.5-.6 3.5-2" stroke="#A89F86" strokeWidth="1.8" strokeLinecap="round" />
    </ToolbarIcon>
  );
}

function ImageIcon() {
  return (
    <ToolbarIcon>
      <rect x="3.5" y="5" width="17" height="14" rx="2" stroke="#A89F86" strokeWidth="1.8" />
      <circle cx="9" cy="10" r="1.6" stroke="#A89F86" strokeWidth="1.8" />
      <path d="M5 17l4.5-4.5a2 2 0 0 1 2.8 0L15 15.5m2-2l1-1a2 2 0 0 1 2.8 0L21 13" stroke="#A89F86" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </ToolbarIcon>
  );
}

function UndoIcon() {
  return (
    <ToolbarIcon>
      <path d="M8 8H5V5" stroke="#A89F86" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.5 8c1.6-2.4 4.3-4 7.5-4a8 8 0 1 1-7.6 10.8" stroke="#A89F86" strokeWidth="1.8" strokeLinecap="round" />
    </ToolbarIcon>
  );
}

function RedoIcon() {
  return (
    <ToolbarIcon>
      <path d="M16 8h3V5" stroke="#A89F86" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18.5 8c-1.6-2.4-4.3-4-7.5-4a8 8 0 1 0 7.6 10.8" stroke="#A89F86" strokeWidth="1.8" strokeLinecap="round" />
    </ToolbarIcon>
  );
}

function ExpandIcon() {
  return (
    <ToolbarIcon>
      <path d="M9 4H4v5M15 4h5v5M9 20H4v-5M15 20h5v-5" stroke="#A89F86" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </ToolbarIcon>
  );
}

function CollapseIcon() {
  return (
    <ToolbarIcon>
      <path d="M4 9h5V4M20 9h-5V4M4 15h5v5M20 15h-5v5" stroke="#A89F86" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </ToolbarIcon>
  );
}

function CloseIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 5l14 14M19 5L5 19" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function ToolbarButton({ onClick, title, active, children }: { onClick: () => void; title: string; active?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 28, borderRadius: 6, border: 'none',
        background: active ? 'rgba(201,168,76,0.18)' : 'transparent',
        cursor: 'pointer', flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

function safeQueryCommandState(command: string): boolean {
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
}

interface Props {
  /**
   * Performs the actual send (FriendContext.sendMessage plus any host
   * follow-up). MUST throw/reject on failure — the composer then restores
   * the cleared content and shows the shared localized error toast, which is
   * what guarantees exact send-once behavior with restored drafts on failure.
   */
  onSend: (body: string, image: File | undefined) => Promise<void>;
  /** Dock windows: tighter toolbar (no link/hashtag/undo/redo/fullscreen). */
  compact?: boolean;
  /** Screen only — the fullscreen toggle (state owned by the host). */
  allowFullscreen?: boolean;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
  /** Draft persistence (dock): initial editor HTML + change notifications.
   *  Absent on the /messages screen ⇒ behavior byte-identical to before. */
  initialHtml?: string;
  onDraftChange?: (html: string) => void;
}

/**
 * The ONE shared direct-message composer — extracted verbatim from the
 * /messages MessageThread. Owns text/image state, validation (type/size),
 * the rich-text toolbar, emoji picker, send-once semantics and localized
 * error rendering; both the legacy screen and the dock windows compose
 * through this single implementation (no copy-pasted send/upload paths).
 */
export function DirectMessageComposer({
  onSend,
  compact,
  allowFullscreen,
  fullscreen,
  onToggleFullscreen,
  initialHtml,
  onDraftChange,
}: Props) {
  const { t } = useTranslation('messages');
  const [text, setText]       = useState('');
  const [sending, setSending] = useState(false);
  const [image, setImage]             = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [emojiOpen, setEmojiOpen]     = useState(false);
  const [linkOpen, setLinkOpen]       = useState(false);
  const [linkValue, setLinkValue]     = useState('');
  const [isEmpty, setIsEmpty]         = useState(true);
  const [activeFormats, setActiveFormats] = useState({ bold: false, italic: false, underline: false });
  const editableRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiPopoverRef = useRef<HTMLDivElement>(null);
  const linkPopoverRef = useRef<HTMLDivElement>(null);

  // Restore a preserved draft exactly once per mount (dock restore path).
  const initialHtmlRef = useRef(initialHtml);
  useEffect(() => {
    const el = editableRef.current;
    const html = initialHtmlRef.current;
    if (el && html) {
      el.innerHTML = html;
      setText(htmlToFormattedText(el));
      setIsEmpty((el.textContent ?? '').trim() === '');
    }
  }, []);

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (emojiOpen && emojiPopoverRef.current && !emojiPopoverRef.current.contains(e.target as Node)) setEmojiOpen(false);
      if (linkOpen && linkPopoverRef.current && !linkPopoverRef.current.contains(e.target as Node)) setLinkOpen(false);
    }
    if (emojiOpen || linkOpen) document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [emojiOpen, linkOpen]);

  useEffect(() => {
    return () => { if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl); };
  }, [imagePreviewUrl]);

  useEffect(() => {
    function onSelectionChange() {
      if (document.activeElement === editableRef.current) updateActiveFormats();
    }
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, []);

  function updateActiveFormats() {
    setActiveFormats({
      bold:      safeQueryCommandState('bold'),
      italic:    safeQueryCommandState('italic'),
      underline: safeQueryCommandState('underline'),
    });
  }

  function syncFromDom() {
    const el = editableRef.current;
    if (!el) return;
    setText(htmlToFormattedText(el));
    setIsEmpty((el.textContent ?? '').trim() === '');
    onDraftChange?.(el.innerHTML);
  }

  function applyFormat(command: 'bold' | 'italic' | 'underline') {
    const el = editableRef.current;
    if (!el) return;
    el.focus();
    document.execCommand(command);
    syncFromDom();
    updateActiveFormats();
  }

  function insertPlainText(str: string) {
    const el = editableRef.current;
    if (!el) return;
    el.focus();
    document.execCommand('insertText', false, str);
    syncFromDom();
  }

  function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    e.preventDefault();
    insertPlainText(e.clipboardData.getData('text/plain'));
  }

  function handleUndo() {
    editableRef.current?.focus();
    document.execCommand('undo');
    syncFromDom();
  }

  function handleRedo() {
    editableRef.current?.focus();
    document.execCommand('redo');
    syncFromDom();
  }

  function handleEmojiClick(emojiData: EmojiClickData) {
    insertPlainText(emojiData.emoji);
    setEmojiOpen(false);
  }

  function handleInsertLink() {
    const url = linkValue.trim();
    if (!url) return;
    insertPlainText(url.startsWith('http') ? url : `https://${url}`);
    setLinkValue('');
    setLinkOpen(false);
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      showSocialToast(t('errors.imageType'));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      showSocialToast(t('errors.imageSize'));
      return;
    }
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImage(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  }

  function clearImage() {
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImage(null);
    setImagePreviewUrl(null);
  }

  async function handleSend() {
    const body = text.trim();
    if ((!body && !image) || sending) return;
    setSending(true);
    const pendingImage = image;
    const pendingHtml = editableRef.current?.innerHTML ?? '';
    if (editableRef.current) editableRef.current.innerHTML = '';
    setText('');
    setIsEmpty(true);
    onDraftChange?.('');
    clearImage();
    try {
      await onSend(body, pendingImage ?? undefined);
    } catch (e) {
      // Phase 12 (C3a): coded errors localize via the catalog; uncoded
      // backend prose stays verbatim; the generic case is ours.
      showSocialToast(renderUiMessage(t, uiFromError(e, 'errors.send')));
      if (editableRef.current) editableRef.current.innerHTML = pendingHtml;
      syncFromDom();
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (e.shiftKey) {
      document.execCommand('insertLineBreak');
      syncFromDom();
    } else {
      void handleSend();
    }
  }

  return (
    <div style={{ position: 'relative', borderRadius: DT.radius.sm, border: '1px solid rgba(245,245,245,0.15)', background: 'rgba(255,255,255,0.06)' }}>
      {imagePreviewUrl && (
        <div style={{ padding: '10px 10px 0' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img src={imagePreviewUrl} alt="" style={{ maxHeight: 90, borderRadius: 8, display: 'block' }} />
            <button
              type="button"
              aria-label={t('removeImage')}
              onClick={clearImage}
              style={{
                position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%',
                background: '#3A3A3A', border: '1px solid rgba(255,255,255,0.2)', display: 'flex',
                alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              }}
            >
              <CloseIcon />
            </button>
          </div>
        </div>
      )}

      <div style={{ position: 'relative' }}>
        {isEmpty && (
          <div
            aria-hidden="true"
            style={{
              position:      'absolute',
              top:           10,
              left:          14,
              right:         14,
              color:         '#6B6B6B',
              fontFamily:    DS2.font.body,
              fontSize:      13.5,
              pointerEvents: 'none',
              userSelect:    'none',
            }}
          >
            {t('composerPlaceholder')}
          </div>
        )}
        <div
          ref={editableRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={t('composerAria')}
          onInput={syncFromDom}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onMouseUp={updateActiveFormats}
          onKeyUp={updateActiveFormats}
          onFocus={updateActiveFormats}
          style={{
            width:        '100%',
            boxSizing:    'border-box',
            minHeight:    36,
            maxHeight:    EDITOR_MAX_HEIGHT,
            overflowY:    'auto',
            padding:      '10px 14px 4px',
            outline:      'none',
            color:        '#FFFFFF',
            fontFamily:   DS2.font.body,
            fontSize:     13.5,
            lineHeight:   1.4,
            whiteSpace:   'pre-wrap',
            wordBreak:    'break-word',
          }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '2px 8px 8px' }}>
        <ToolbarButton title={t('toolbar.bold')} active={activeFormats.bold} onClick={() => applyFormat('bold')}><BoldIcon /></ToolbarButton>
        <ToolbarButton title={t('toolbar.italic')} active={activeFormats.italic} onClick={() => applyFormat('italic')}><ItalicIcon /></ToolbarButton>
        <ToolbarButton title={t('toolbar.underline')} active={activeFormats.underline} onClick={() => applyFormat('underline')}><UnderlineIcon /></ToolbarButton>
        {!compact && (
          <>
            <div ref={linkPopoverRef} style={{ position: 'relative' }}>
              <ToolbarButton title={t('toolbar.insertLink')} active={linkOpen} onClick={() => { setLinkOpen(o => !o); setEmojiOpen(false); }}><LinkIcon /></ToolbarButton>
              {linkOpen && (
                <div style={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: 0, display: 'flex', gap: 6, padding: 8, background: '#161616', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.5)', zIndex: 20 }}>
                  <input
                    type="text"
                    autoFocus
                    value={linkValue}
                    onChange={e => setLinkValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleInsertLink(); } }}
                    placeholder="https://…"
                    style={{ width: 180, border: 'none', outline: 'none', background: 'rgba(255,255,255,0.08)', borderRadius: 6, padding: '6px 8px', color: '#FFFFFF', fontFamily: DS2.font.body, fontSize: 12.5 }}
                  />
                  <button type="button" onClick={handleInsertLink} style={{ border: 'none', borderRadius: 6, padding: '0 10px', background: DS2.gradient.gold, color: '#0B0B0B', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    {t('linkAdd')}
                  </button>
                </div>
              )}
            </div>
            <ToolbarButton title={t('toolbar.hashtag')} onClick={() => insertPlainText('#')}><HashtagIcon /></ToolbarButton>
          </>
        )}

        <div style={{ width: 1, height: 18, background: 'rgba(245,245,245,0.12)', margin: '0 4px' }} />

        <div ref={emojiPopoverRef} style={{ position: 'relative' }}>
          <ToolbarButton title={t('toolbar.emoji')} active={emojiOpen} onClick={() => { setEmojiOpen(o => !o); setLinkOpen(false); }}><EmojiIcon /></ToolbarButton>
          {emojiOpen && (
            <div style={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: compact ? undefined : 0, right: compact ? 0 : undefined, zIndex: 20 }}>
              <EmojiPicker onEmojiClick={handleEmojiClick} theme={Theme.DARK} height={compact ? 300 : 360} width={compact ? 270 : 290} />
            </div>
          )}
        </div>
        <ToolbarButton title={t('toolbar.attachImage')} onClick={() => fileInputRef.current?.click()}><ImageIcon /></ToolbarButton>
        <input ref={fileInputRef} type="file" accept={ALLOWED_IMAGE_TYPES.join(',')} style={{ display: 'none' }} onChange={handleFileChange} />

        <div style={{ flex: 1 }} />

        {!compact && (
          <>
            <ToolbarButton title={t('toolbar.undo')} onClick={handleUndo}><UndoIcon /></ToolbarButton>
            <ToolbarButton title={t('toolbar.redo')} onClick={handleRedo}><RedoIcon /></ToolbarButton>
          </>
        )}
        {allowFullscreen && (
          <ToolbarButton title={fullscreen ? t('toolbar.exitFullscreen') : t('toolbar.fullscreen')} onClick={() => onToggleFullscreen?.()}>
            {fullscreen ? <CollapseIcon /> : <ExpandIcon />}
          </ToolbarButton>
        )}

        <button
          onClick={() => void handleSend()}
          disabled={(!text.trim() && !image) || sending}
          style={{
            marginLeft:   4,
            padding:      '7px 18px',
            borderRadius: DT.radius.sm,
            border:       'none',
            background:   DS2.gradient.gold,
            color:        '#0B0B0B',
            fontSize:     13,
            fontWeight:   700,
            fontFamily:   DS2.font.body,
            cursor:       (!text.trim() && !image) || sending ? 'default' : 'pointer',
            opacity:      (!text.trim() && !image) || sending ? 0.5 : 1,
            whiteSpace:   'nowrap',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t('send')}
        </button>
      </div>
    </div>
  );
}
